import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  createIncidentSavedView,
  deleteIncidentSavedView,
  getIncidentAdminTaxonomy,
  getIncidentNotificationRoutingConfig,
  getIncidentReportingOverview,
  getIncidentReportingTrends,
  getIncidentReportingWorkload,
  getIncidentSavedViews,
  getIncidentWorkflowConfig,
  getIncidents,
  updateIncidentSavedView,
} from "./api/incidents.api";
import IncidentDetailDrawer from "./IncidentDetailDrawer";
import IncidentForm from "./IncidentForm";
import { canEditIncidents, canManageIncidents, canViewIncidents } from "./accessControl";
import Loader from "./components/common/Loader";
import EmptyState from "./components/common/EmptyState";
import { useAuth } from "./useAuth";
import Button from "./ui/Button";
import { Input, Select, StatusBadge, Table } from "./ui";
import { colors, radius, shadows, spacing, typography } from "./ui/tokens";
import { surfaceCardBodyStyle, surfaceCardStyle } from "./ui/pagePatterns";

const defaultTaxonomyConfig = {
  incident_type: [
    { code: "Operational", label: "Operational", display_order: 0, active: true, description: "", is_system: true },
    { code: "Security", label: "Security", display_order: 1, active: true, description: "", is_system: true },
    { code: "Privacy", label: "Privacy", display_order: 2, active: true, description: "", is_system: true },
    { code: "Compliance", label: "Compliance", display_order: 3, active: true, description: "", is_system: true },
  ],
  category: [
    { code: "IT", label: "IT", display_order: 0, active: true, description: "", is_system: true },
    { code: "Security", label: "Security", display_order: 1, active: true, description: "", is_system: true },
    { code: "ThirdParty", label: "Third Party", display_order: 2, active: true, description: "", is_system: true },
    { code: "Operations", label: "Operations", display_order: 3, active: true, description: "", is_system: true },
  ],
  severity: [
    { code: "Low", label: "Low", display_order: 0, active: true, description: "", is_system: true },
    { code: "Medium", label: "Medium", display_order: 1, active: true, description: "", is_system: true },
    { code: "High", label: "High", display_order: 2, active: true, description: "", is_system: true },
    { code: "Critical", label: "Critical", display_order: 3, active: true, description: "", is_system: true },
  ],
  priority: [
    { code: "Low", label: "Low", display_order: 0, active: true, description: "", is_system: true },
    { code: "Medium", label: "Medium", display_order: 1, active: true, description: "", is_system: true },
    { code: "High", label: "High", display_order: 2, active: true, description: "", is_system: true },
    { code: "Critical", label: "Critical", display_order: 3, active: true, description: "", is_system: true },
  ],
};
const defaultWorkflowConfig = {
  statuses: [
    { code: "New", label: "New", display_order: 0, active: true, is_terminal: false, description: "", is_system: true },
    { code: "Investigating", label: "Investigating", display_order: 1, active: true, is_terminal: false, description: "", is_system: true },
    { code: "Mitigated", label: "Mitigated", display_order: 2, active: true, is_terminal: false, description: "", is_system: true },
    { code: "Closed", label: "Closed", display_order: 3, active: true, is_terminal: true, description: "", is_system: true },
  ],
  transitions: [
    { from_status: "New", to_status: "Investigating", required_fields: [], active: true, is_system: true },
    { from_status: "New", to_status: "Mitigated", required_fields: [], active: true, is_system: true },
    { from_status: "Investigating", to_status: "New", required_fields: [], active: true, is_system: true },
    { from_status: "Investigating", to_status: "Mitigated", required_fields: [], active: true, is_system: true },
    { from_status: "Mitigated", to_status: "New", required_fields: [], active: true, is_system: true },
    { from_status: "Mitigated", to_status: "Investigating", required_fields: [], active: true, is_system: true },
  ],
};
const defaultNotificationRouting = {
  routes: [
    { event_type: "incident.created", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.assigned", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.status_changed", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.sla_breached", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.escalated", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.escalated", enabled: true, recipient_type: "escalation_target", recipient_identifier: "", channel: "in_app", active: true, display_order: 1, is_system: true },
    { event_type: "incident.closed", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.reopened", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
  ],
};
const sortOptions = [
  { value: "reported_at:desc", label: "Latest reported" },
  { value: "created_at:desc", label: "Newest created" },
  { value: "closed_at:desc", label: "Recently closed" },
  { value: "severity:desc", label: "Highest severity" },
  { value: "priority:desc", label: "Highest priority" },
];

const containerStyle = {
  display: "grid",
  gap: spacing.xl,
};

const cardStyle = {
  ...surfaceCardStyle,
};

const tableCardBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.md,
};

const defaultFilters = {
  q: "",
  status: "",
  severity: "",
  severity_values: [],
  priority: "",
  owner_id: "",
  breached: false,
  escalated: false,
  open_only: false,
  created_from: "",
  created_to: "",
  sort: "reported_at:desc",
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const extractApiError = (err, fallback) => {
  const source = err?.error || err?.detail || err;
  if (typeof source === "string" && source.trim()) return source;
  if (typeof source?.detail === "string" && source.detail.trim()) return source.detail;
  if (source?.field_errors && typeof source.field_errors === "object") {
    const message = Object.values(source.field_errors).find((value) => typeof value === "string" && value.trim());
    if (message) return message;
  }
  return fallback;
};

const normalizeSeverityValues = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeFilterPayload = (payload = {}) => ({
  ...defaultFilters,
  ...payload,
  severity: typeof payload?.severity === "string" ? payload.severity : defaultFilters.severity,
  severity_values: normalizeSeverityValues(payload?.severity_values),
  breached: Boolean(payload?.breached),
  escalated: Boolean(payload?.escalated),
  open_only: Boolean(payload?.open_only),
  created_from: payload?.created_from || "",
  created_to: payload?.created_to || "",
});

const REGISTER_PAGE_SIZE = 50;
const DEFAULT_REGISTER_PAGE_STATE = {
  total: 0,
  limit: REGISTER_PAGE_SIZE,
  offset: 0,
};

const buildListParams = (filters, offset = 0, limit = REGISTER_PAGE_SIZE) => ({
  q: filters.q || undefined,
  status: filters.status || undefined,
  severity: filters.severity || undefined,
  severity_values: filters.severity_values?.length ? filters.severity_values.join(",") : undefined,
  priority: filters.priority || undefined,
  owner_id: filters.owner_id || undefined,
  breached: filters.breached || undefined,
  escalated: filters.escalated || undefined,
  open_only: filters.open_only || undefined,
  created_from: filters.created_from || undefined,
  created_to: filters.created_to || undefined,
  sort: filters.sort || "reported_at:desc",
  limit,
  offset,
});

const MetricCard = ({ label, value, hint }) => (
  <div
    style={{
      border: `1px solid ${colors.border}`,
      borderRadius: radius.md,
      padding: `${spacing.md + spacing.xs}px ${spacing.xl}px`,
      background: colors.surfaceSubtle,
      boxShadow: shadows.xs,
    }}
  >
    <div
      style={{
        fontSize: typography.size.xs,
        color: colors.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: typography.size.xl, fontWeight: typography.weight.bold, color: colors.text }}>{value}</div>
    {hint && <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{hint}</div>}
  </div>
);

const tabRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.md,
};

const registerToolbarStyle = {
  display: "grid",
  gap: spacing.sm,
};

const registerToolbarRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
  alignItems: "center",
};

const registerToolbarSearchStyle = {
  flex: "2 1 280px",
  minWidth: 220,
};

const registerToolbarSelectStyle = {
  flex: "1 1 180px",
  minWidth: 160,
};

const registerToolbarMenuStyle = {
  position: "relative",
};

const registerToolbarMenuPanelStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  zIndex: 30,
  minWidth: 320,
  width: "min(420px, calc(100vw - 48px))",
  padding: spacing.md,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.md,
  display: "grid",
  gap: spacing.md,
};

const registerToolbarMenuGridStyle = {
  display: "grid",
  gap: spacing.sm,
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
};

const registerToolbarCheckboxStyle = {
  display: "flex",
  gap: spacing.sm,
  alignItems: "center",
  fontSize: typography.size.sm,
  color: colors.text,
};

const registerToolbarMetaTextStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const registerQuickViewsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
};

const registerQuickViewButtonsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const registerOperationalSummaryStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const registerOperationalPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.sm}px`,
  borderRadius: radius.sm,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
};

const registerTableSelectedRowStyle = {
  background: colors.accentSubtle,
  boxShadow: `inset 3px 0 0 ${colors.accent}`,
};

const registerTableRowStyle = {
  cursor: "pointer",
};

const opsSummaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: spacing.sm,
};

const opsSummaryBlockStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  padding: spacing.md,
  display: "grid",
  gap: 2,
};

const opsSummaryCountStyle = {
  fontSize: typography.size.xl,
  fontWeight: typography.weight.bold,
  color: colors.text,
};

const opsSummaryLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const opsQueueButtonsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const opsPaginationBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const opsSignalGroupStyle = {
  display: "flex",
  gap: spacing.xs,
  flexWrap: "wrap",
  alignItems: "center",
};

const opsStatePillBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.sm}px`,
  borderRadius: radius.pill,
  borderWidth: "1px",
  borderStyle: "solid",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  whiteSpace: "nowrap",
};

const opsSubtitleStyle = {
  fontSize: typography.size.sm,
  color: colors.textMuted,
};

const INCIDENTS_BASE_ROUTE = "/incidents";
const DEFAULT_OPS_QUEUE = "needs-attention";
const OPS_HEALTH_PAGE_SIZE = 50;
const DEFAULT_OPS_SUMMARY = {
  breachedCount: null,
  escalatedCount: null,
  highCriticalCount: null,
};
const DEFAULT_OPS_QUEUE_STATE = {
  items: [],
  total: 0,
  limit: OPS_HEALTH_PAGE_SIZE,
  offset: 0,
};

const opsQueueDefinitions = [
  {
    key: "needs-attention",
    label: "Needs Attention",
    emptyTitle: "No incidents requiring attention",
    emptyDescription: "No breached, escalated or High/Critical open incidents currently require attention.",
  },
  {
    key: "breached",
    label: "Breached",
    emptyTitle: "No breached incidents",
    emptyDescription: "No open incidents are currently breached.",
  },
  {
    key: "escalated",
    label: "Escalated",
    emptyTitle: "No escalated incidents",
    emptyDescription: "No open incidents are currently escalated.",
  },
  {
    key: "high-critical",
    label: "High/Critical",
    emptyTitle: "No High/Critical open incidents",
    emptyDescription: "No open High or Critical incidents currently require attention.",
  },
];


const isIncidentEscalated = (incident) =>
  Boolean(incident?.escalated_at || Number(incident?.escalation_level || 0) > 0);

const isIncidentBreached = (incident) => Boolean(incident?.sla_breached || incident?.breach_at);

const buildOpsQueueParams = (queueKey, offset = 0) => {
  const params = {
    limit: OPS_HEALTH_PAGE_SIZE,
    offset,
    sort: "reported_at:desc",
  };
  if (queueKey === "breached") {
    return {
      ...params,
      open_only: true,
      breached: true,
    };
  }
  if (queueKey === "escalated") {
    return {
      ...params,
      open_only: true,
      escalated: true,
    };
  }
  if (queueKey === "high-critical") {
    return {
      ...params,
      open_only: true,
      severity_values: "High,Critical",
    };
  }
  return {
    ...params,
    needs_attention: true,
  };
};

const formatOpsCount = (value) => (Number.isFinite(value) ? value : "-");

const countAdvancedFilters = (filters) => {
  let count = 0;
  if (filters.priority) count += 1;
  if (filters.owner_id) count += 1;
  if (filters.created_from) count += 1;
  if (filters.created_to) count += 1;
  if (filters.breached) count += 1;
  if (filters.escalated) count += 1;
  if ((filters.sort || defaultFilters.sort) !== defaultFilters.sort) count += 1;
  return count;
};

export default function IncidentRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const { incidentId: routeIncidentId } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("register");
  const [incidents, setIncidents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [registerPage, setRegisterPage] = useState(DEFAULT_REGISTER_PAGE_STATE);
  const [savedViews, setSavedViews] = useState([]);
  const [selectedSavedViewId, setSelectedSavedViewId] = useState("");
  const [savedViewName, setSavedViewName] = useState("");
  const [activeQuickView, setActiveQuickView] = useState("");
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [isSavedViewsOpen, setIsSavedViewsOpen] = useState(false);
  const [reporting, setReporting] = useState({ overview: null, trends: null, workload: null });
  const [opsSummary, setOpsSummary] = useState(DEFAULT_OPS_SUMMARY);
  const [opsSummaryError, setOpsSummaryError] = useState("");
  const [opsQueue, setOpsQueue] = useState(DEFAULT_OPS_QUEUE_STATE);
  const [opsNeedsAttentionTotal, setOpsNeedsAttentionTotal] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsQueueError, setOpsQueueError] = useState("");
  const [activeOpsQueue, setActiveOpsQueue] = useState(DEFAULT_OPS_QUEUE);
  const [opsQueueOffset, setOpsQueueOffset] = useState(0);
  const [adminConfig, setAdminConfig] = useState({
    taxonomy: defaultTaxonomyConfig,
    workflow: defaultWorkflowConfig,
    notificationRouting: defaultNotificationRouting,
  });
  const moreFiltersRef = useRef(null);
  const savedViewsRef = useRef(null);
  const opsSummaryRequestRef = useRef(0);
  const opsQueueRequestRef = useRef(0);
  const opsNeedsAttentionTotalRequestRef = useRef(0);

  const canView = useMemo(() => {
    if (!user) return true;
    return canViewIncidents(user);
  }, [user]);

  const { canEdit, canManage, isAdmin } = useMemo(() => {
    if (!user) {
      return { canEdit: true, canManage: true, isAdmin: true };
    }
    const permissions = new Set(user.permissions || []);
    const role = String(user.role || "").toUpperCase();
    return {
      canEdit: canEditIncidents(user),
      canManage: canManageIncidents(user),
      isAdmin: role === "ADMIN" || permissions.has("incident.admin"),
    };
  }, [user]);

  const statusOptions = useMemo(() => {
    const configured = (adminConfig.workflow?.statuses || [])
      .filter((item) => item.active)
      .map((item) => item.code);
    return ["", ...(configured.length > 0 ? configured : defaultWorkflowConfig.statuses.map((item) => item.code))];
  }, [adminConfig.workflow]);

  const severityOptions = useMemo(() => {
    const configured = (adminConfig.taxonomy?.severity || [])
      .filter((item) => item.active)
      .map((item) => item.code);
    return ["", ...(configured.length > 0 ? configured : defaultTaxonomyConfig.severity.map((item) => item.code))];
  }, [adminConfig.taxonomy]);

  const priorityOptions = useMemo(() => {
    const configured = (adminConfig.taxonomy?.priority || [])
      .filter((item) => item.active)
      .map((item) => item.code);
    return ["", ...(configured.length > 0 ? configured : defaultTaxonomyConfig.priority.map((item) => item.code))];
  }, [adminConfig.taxonomy]);

  const isIncidentRoute =
    location.pathname === INCIDENTS_BASE_ROUTE || location.pathname.startsWith(`${INCIDENTS_BASE_ROUTE}/`);
  const selectedIncidentId = routeIncidentId || selectedId;
  const incidentDrawerCollection = activeTab === "ops" ? opsQueue.items : incidents;

  const selectedIncident = useMemo(
    () => incidentDrawerCollection.find((inc) => String(inc.id) === String(selectedIncidentId)) || null,
    [incidentDrawerCollection, selectedIncidentId]
  );

  const selectedSavedView = useMemo(
    () => savedViews.find((view) => String(view.id) === String(selectedSavedViewId)) || null,
    [savedViews, selectedSavedViewId]
  );

  const loadIncidents = async (activeFilters = filters, offset = registerPage.offset) => {
    setLoading(true);
    setError("");
    try {
      const res = await getIncidents(buildListParams(activeFilters, offset));
      const payload = res.data || res;
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const total = Number.isFinite(payload?.total) ? payload.total : items.length;
      const limit = Number.isFinite(payload?.limit) ? payload.limit : REGISTER_PAGE_SIZE;
      const resolvedOffset = Number.isFinite(payload?.offset) ? payload.offset : offset;
      if (total > 0 && items.length === 0 && resolvedOffset > 0) {
        const lastValidOffset = Math.max(0, Math.floor((total - 1) / Math.max(limit, 1)) * limit);
        if (lastValidOffset !== resolvedOffset) {
          setRegisterPage((current) => ({
            ...current,
            total,
            limit,
            offset: lastValidOffset,
          }));
          return;
        }
      }
      setIncidents(items);
      setRegisterPage({
        total,
        limit,
        offset: resolvedOffset,
      });
      setSelectedId((currentSelectedId) => {
        if (!items.length) return null;
        return items.find((inc) => String(inc.id) === String(currentSelectedId)) ? currentSelectedId : null;
      });
    } catch (err) {
      setError(extractApiError(err, "Failed to load incidents"));
      setIncidents([]);
      setRegisterPage((current) => ({
        ...current,
        total: 0,
        offset: 0,
      }));
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadReporting = async (activeFilters = filters) => {
    const params = {
      date_from: activeFilters.created_from || undefined,
      date_to: activeFilters.created_to || undefined,
      status: activeFilters.status || undefined,
      severity: activeFilters.severity || undefined,
      severity_values: activeFilters.severity_values?.length ? activeFilters.severity_values.join(",") : undefined,
      priority: activeFilters.priority || undefined,
      owner_id: activeFilters.owner_id || undefined,
      breached_only: activeFilters.breached || undefined,
      escalated: activeFilters.escalated || undefined,
      open_only: activeFilters.open_only || undefined,
    };
    try {
      const [overviewRes, trendsRes, workloadRes] = await Promise.all([
        getIncidentReportingOverview(params),
        getIncidentReportingTrends(params),
        getIncidentReportingWorkload(params),
      ]);
      setReporting({
        overview: overviewRes.data || overviewRes,
        trends: trendsRes.data || trendsRes,
        workload: workloadRes.data || workloadRes,
      });
    } catch (_err) {
      setReporting({ overview: null, trends: null, workload: null });
    }
  };

  const loadSavedViews = async () => {
    try {
      const res = await getIncidentSavedViews();
      setSavedViews(res.data || res);
    } catch (_err) {
      setSavedViews([]);
    }
  };

  const loadOpsSummary = async () => {
    const requestId = opsSummaryRequestRef.current + 1;
    opsSummaryRequestRef.current = requestId;
    setOpsSummaryError("");
    try {
      const res = await getIncidentReportingOverview({ open_only: true });
      if (requestId !== opsSummaryRequestRef.current) return;
      const payload = res.data || res;
      const openBySeverity = payload?.open_by_severity || {};
      setOpsSummary({
        breachedCount: Number(payload?.breached_count || 0),
        escalatedCount: Number(payload?.escalated_count || 0),
        highCriticalCount: Number(openBySeverity.High || 0) + Number(openBySeverity.Critical || 0),
      });
    } catch (_err) {
      if (requestId !== opsSummaryRequestRef.current) return;
      setOpsSummary(DEFAULT_OPS_SUMMARY);
      setOpsSummaryError("Unable to load Ops Health summary right now.");
    }
  };

  const loadOpsNeedsAttentionTotal = async () => {
    const requestId = opsNeedsAttentionTotalRequestRef.current + 1;
    opsNeedsAttentionTotalRequestRef.current = requestId;
    try {
      const res = await getIncidents({
        ...buildOpsQueueParams(DEFAULT_OPS_QUEUE, 0),
        limit: 1,
      });
      if (requestId !== opsNeedsAttentionTotalRequestRef.current) return;
      const payload = res.data || res;
      const total = Number.isFinite(payload?.total) ? payload.total : (payload?.items || []).length;
      setOpsNeedsAttentionTotal(total);
    } catch (_err) {
      if (requestId !== opsNeedsAttentionTotalRequestRef.current) return;
      setOpsNeedsAttentionTotal(null);
    }
  };

  const loadOpsQueue = async (queueKey = activeOpsQueue, offset = opsQueueOffset) => {
    const requestId = opsQueueRequestRef.current + 1;
    opsQueueRequestRef.current = requestId;
    setOpsLoading(true);
    setOpsQueueError("");
    try {
      const res = await getIncidents(buildOpsQueueParams(queueKey, offset));
      if (requestId !== opsQueueRequestRef.current) return;
      const payload = res.data || res;
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const total = Number.isFinite(payload?.total) ? payload.total : items.length;
      const limit = Number.isFinite(payload?.limit) ? payload.limit : OPS_HEALTH_PAGE_SIZE;
      const resolvedOffset = Number.isFinite(payload?.offset) ? payload.offset : offset;
      setOpsQueue({
        items,
        total,
        limit,
        offset: resolvedOffset,
      });
      if (queueKey === DEFAULT_OPS_QUEUE) {
        setOpsNeedsAttentionTotal(total);
      }
      setSelectedId((currentSelectedId) => {
        if (!items.length) return null;
        return items.find((incident) => String(incident.id) === String(currentSelectedId)) ? currentSelectedId : null;
      });
    } catch (_err) {
      if (requestId !== opsQueueRequestRef.current) return;
      setOpsQueue(DEFAULT_OPS_QUEUE_STATE);
      if (queueKey === DEFAULT_OPS_QUEUE) {
        setOpsNeedsAttentionTotal(null);
      }
      setOpsQueueError("Unable to load Ops Health right now.");
      setSelectedId(null);
    } finally {
      if (requestId === opsQueueRequestRef.current) {
        setOpsLoading(false);
      }
    }
  };

  const loadAdminConfig = async () => {
    try {
      const [typeRes, categoryRes, severityRes, priorityRes, workflowRes] = await Promise.all([
        getIncidentAdminTaxonomy("incident_type"),
        getIncidentAdminTaxonomy("category"),
        getIncidentAdminTaxonomy("severity"),
        getIncidentAdminTaxonomy("priority"),
        getIncidentWorkflowConfig(),
      ]);
      const notificationRes = isAdmin ? await getIncidentNotificationRoutingConfig() : { data: defaultNotificationRouting };
      setAdminConfig({
        taxonomy: {
          incident_type: typeRes.data || typeRes,
          category: categoryRes.data || categoryRes,
          severity: severityRes.data || severityRes,
          priority: priorityRes.data || priorityRes,
        },
        workflow: workflowRes.data || workflowRes,
        notificationRouting: notificationRes.data || notificationRes,
      });
    } catch (_err) {
      setAdminConfig({
        taxonomy: defaultTaxonomyConfig,
        workflow: defaultWorkflowConfig,
        notificationRouting: defaultNotificationRouting,
      });
    }
  };

  const refreshAll = async (activeFilters = filters) => {
    await Promise.all([
      loadIncidents(activeFilters, registerPage.offset),
      loadReporting(activeFilters),
      loadSavedViews(),
      loadAdminConfig(),
    ]);
  };

  useEffect(() => {
    if (!canView) return;
    Promise.all([loadSavedViews(), loadAdminConfig()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, isAdmin]);

  useEffect(() => {
    if (!canView) return;
    loadReporting(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!canView) return;
    loadIncidents(filters, registerPage.offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, registerPage.offset]);

  useEffect(() => {
    if (!canView || activeTab !== "ops") return;
    loadOpsSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "ops") return;
    loadOpsQueue(activeOpsQueue, opsQueueOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canView, activeOpsQueue, opsQueueOffset]);

  useEffect(() => {
    if (canEdit) return;
    setShowCreate(false);
    setEditingIncident(null);
  }, [canEdit]);

  useEffect(() => {
    if (routeIncidentId) {
      setSelectedId(null);
    }
  }, [routeIncidentId]);

  useEffect(() => {
    if (activeTab !== "register") {
      setSelectedId(null);
    }
  }, [activeTab]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const target = event.target;
      if (moreFiltersRef.current?.contains(target) || savedViewsRef.current?.contains(target)) {
        return;
      }
      setIsMoreFiltersOpen(false);
      setIsSavedViewsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMoreFiltersOpen(false);
        setIsSavedViewsOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleIncidentUpdated = async (updated) => {
    if (updated?.id) {
      setSelectedId(updated.id);
    }
    await Promise.all([
      refreshAll(filters),
      activeTab === "ops"
        ? Promise.all([
            loadOpsSummary(),
            loadOpsQueue(activeOpsQueue, opsQueueOffset),
            activeOpsQueue === DEFAULT_OPS_QUEUE ? Promise.resolve() : loadOpsNeedsAttentionTotal(),
          ])
        : Promise.resolve(),
    ]);
  };

  const resetRegisterOffset = () => {
    setRegisterPage((current) =>
      current.offset === 0
        ? current
        : {
            ...current,
            offset: 0,
          }
    );
  };

  const applySavedView = (view) => {
    if (!view) return;
    setActiveQuickView("");
    setIsSavedViewsOpen(false);
    setSelectedSavedViewId(String(view.id));
    setSavedViewName(view.name || "");
    resetRegisterOffset();
    setFilters((prev) => ({
      ...prev,
      ...normalizeFilterPayload(view.filter_payload || {}),
      sort: view.sort_field && view.sort_direction ? `${view.sort_field}:${view.sort_direction}` : prev.sort,
    }));
  };

  const saveCurrentView = async (mode) => {
    if (!canEdit) return;
    if (!savedViewName.trim()) {
      setError("Saved view name is required");
      return;
    }
    const [sortField, sortDirection] = (filters.sort || "reported_at:desc").split(":");
    const payload = {
      name: savedViewName.trim(),
      visibility: "private",
      filter_payload: {
        q: filters.q || undefined,
        status: filters.status || undefined,
        severity: filters.severity || undefined,
        severity_values: filters.severity_values?.length ? filters.severity_values : undefined,
        priority: filters.priority || undefined,
        owner_id: filters.owner_id || undefined,
        breached: filters.breached || undefined,
        escalated: filters.escalated || undefined,
        open_only: filters.open_only || undefined,
        created_from: filters.created_from || undefined,
        created_to: filters.created_to || undefined,
      },
      sort_field: sortField,
      sort_direction: sortDirection,
    };
    try {
      if (mode === "update" && selectedSavedViewId) {
        await updateIncidentSavedView(selectedSavedViewId, payload);
      } else {
        const res = await createIncidentSavedView(payload);
        const created = res.data || res;
        setSelectedSavedViewId(String(created.id));
      }
      await loadSavedViews();
      setIsSavedViewsOpen(false);
    } catch (err) {
      setError(extractApiError(err, "Failed to save incident view"));
    }
  };

  const removeSavedView = async () => {
    if (!canEdit || !selectedSavedViewId) return;
    try {
      await deleteIncidentSavedView(selectedSavedViewId);
      setSelectedSavedViewId("");
      setSavedViewName("");
      await loadSavedViews();
      setIsSavedViewsOpen(false);
    } catch (err) {
      setError(extractApiError(err, "Failed to delete incident view"));
    }
  };

  const applyQueue = (queue) => {
    setActiveQuickView(queue);
    resetRegisterOffset();
    if (queue === "my-open") {
      setFilters((prev) => ({
        ...defaultFilters,
        owner_id: user?.username || "",
        open_only: true,
        sort: prev.sort,
      }));
      return;
    }
    if (queue === "breached") {
      setFilters((prev) => ({ ...defaultFilters, breached: true, sort: prev.sort }));
      return;
    }
    if (queue === "high-critical") {
      setFilters((prev) => ({
        ...defaultFilters,
        severity_values: ["High", "Critical"],
        open_only: true,
        sort: prev.sort,
      }));
      return;
    }
    if (queue === "recently-closed") {
      setFilters({ ...defaultFilters, status: "Closed", sort: "closed_at:desc" });
    }
  };

  const updateFilters = (updater) => {
    setActiveQuickView("");
    resetRegisterOffset();
    setFilters((prev) => (typeof updater === "function" ? updater(prev) : updater));
  };

  const resetAllFilters = () => {
    setActiveQuickView("");
    resetRegisterOffset();
    setFilters(defaultFilters);
  };

  const workspaceTabs = [
    { key: "register", label: "Incident Register" },
    { key: "reporting", label: "Reporting" },
    { key: "ops", label: "Ops Health" },
  ];

  const incidentColumns = useMemo(
    () => [
      {
        id: "title",
        header: "Incident",
        accessor: "title",
        width: 320,
        render: (incident) => (
          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>
              {incident.title}
            </div>
            <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
              {incident.description || "No description provided."}
            </div>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessor: "status",
        width: 140,
        render: (incident) => <StatusBadge value={incident.status} fallbackLabel={incident.status || "-"} />,
      },
      {
        id: "severity",
        header: "Severity",
        accessor: "severity",
        width: 120,
        render: (incident) =>
          incident.severity ? (
            <StatusBadge
              variant="severity"
              value={incident.severity}
              fallbackLabel={incident.severity}
              label={incident.severity}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "priority",
        header: "Priority",
        accessor: "priority",
        width: 120,
        render: (incident) =>
          incident.priority ? (
            <StatusBadge
              variant="severity"
              value={incident.priority}
              fallbackLabel={incident.priority}
              label={incident.priority}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "category",
        header: "Category",
        accessor: "category",
        width: 140,
        render: (incident) => incident.category || "Uncategorized",
      },
      {
        id: "owner",
        header: "Owner",
        accessor: "owner_id",
        width: 140,
        render: (incident) => incident.owner_id || "Unassigned",
      },
      {
        id: "reported_at",
        header: "Reported",
        accessor: "reported_at",
        width: 180,
        render: (incident) => formatDateTime(incident.reported_at),
      },
    ],
    []
  );

  const advancedFilterCount = useMemo(() => countAdvancedFilters(filters), [filters]);
  const moreFiltersLabel = advancedFilterCount > 0 ? `More Filters (${advancedFilterCount})` : "More Filters";
  const registerCurrentPage = Math.floor(registerPage.offset / Math.max(registerPage.limit, 1)) + 1;
  const registerTotalPages = Math.max(1, Math.ceil(registerPage.total / Math.max(registerPage.limit, 1)));
  const registerPageStart = registerPage.total === 0 ? 0 : registerPage.offset + 1;
  const registerPageEnd = registerPage.total === 0 ? 0 : registerPage.offset + incidents.length;
  const activeOpsDefinition = useMemo(
    () => opsQueueDefinitions.find((queue) => queue.key === activeOpsQueue) || opsQueueDefinitions[0],
    [activeOpsQueue]
  );
  const opsQueueCounts = useMemo(
    () => ({
      "needs-attention": opsNeedsAttentionTotal,
      breached: opsSummary.breachedCount,
      escalated: opsSummary.escalatedCount,
      "high-critical": opsSummary.highCriticalCount,
    }),
    [opsNeedsAttentionTotal, opsSummary]
  );
  const opsCurrentPage = Math.floor(opsQueue.offset / Math.max(opsQueue.limit, 1)) + 1;
  const opsTotalPages = Math.max(1, Math.ceil(opsQueue.total / Math.max(opsQueue.limit, 1)));
  const opsPageStart = opsQueue.total === 0 ? 0 : opsQueue.offset + 1;
  const opsPageEnd = opsQueue.total === 0 ? 0 : opsQueue.offset + opsQueue.items.length;
  const opsNeedsAttentionSubtitle =
    opsNeedsAttentionTotal === null
      ? "Needs Attention totals unavailable right now."
      : `${opsNeedsAttentionTotal} incident${opsNeedsAttentionTotal === 1 ? "" : "s"} currently need attention`;
  const opsIncidentColumns = useMemo(
    () => [
      {
        id: "title",
        header: "Incident",
        accessor: "title",
        width: 320,
        render: (incident) => (
          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{incident.title}</div>
            <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
              {incident.description || "No description provided."}
            </div>
          </div>
        ),
      },
      {
        id: "severity",
        header: "Severity",
        accessor: "severity",
        width: 140,
        render: (incident) =>
          incident.severity ? (
            <StatusBadge
              variant="severity"
              value={incident.severity}
              fallbackLabel={incident.severity}
              label={incident.severity}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "signals",
        header: "Signals",
        accessor: "status",
        width: 260,
        render: (incident) => (
          <div style={opsSignalGroupStyle}>
            {isIncidentBreached(incident) ? (
              <span
                style={{
                  ...opsStatePillBaseStyle,
                  borderColor: colors.danger,
                  background: "rgba(220, 38, 38, 0.08)",
                  color: colors.danger,
                }}
              >
                Breached
              </span>
            ) : null}
            {isIncidentEscalated(incident) ? (
              <span
                style={{
                  ...opsStatePillBaseStyle,
                  borderColor: colors.accent,
                  background: colors.accentSubtle,
                  color: colors.accentActive,
                }}
              >
                Escalated
              </span>
            ) : null}
            <StatusBadge value={incident.status} fallbackLabel={incident.status || "-"} />
          </div>
        ),
      },
      {
        id: "owner",
        header: "Owner",
        accessor: "owner_id",
        width: 160,
        render: (incident) => incident.owner_id || "Unassigned",
      },
      {
        id: "reported_at",
        header: "Reported",
        accessor: "reported_at",
        width: 180,
        render: (incident) => formatDateTime(incident.reported_at),
      },
    ],
    []
  );

  if (!canView) {
    return <div style={{ padding: spacing.xl, color: colors.textMuted }}>You do not have permission to view incidents.</div>;
  }

  return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, ...surfaceCardBodyStyle, display: "grid", gap: spacing.lg }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: spacing.xl,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "grid", gap: spacing.xs }}>
            <div style={{ fontWeight: typography.weight.bold, fontSize: typography.size.lg, color: colors.text }}>
              Incidents
            </div>
            <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
              {registerPage.total} incident{registerPage.total === 1 ? "" : "s"} in the current register view
            </div>
          </div>
          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
            {canEdit ? (
              <Button
                onClick={() => {
                  setEditingIncident(null);
                  setShowCreate(true);
                }}
                variant="primary"
              >
                + Report Incident
              </Button>
            ) : null}
            <Button onClick={() => refreshAll(filters)} variant="secondary">
              Refresh
            </Button>
          </div>
        </div>

        <div style={tabRowStyle} role="tablist" aria-label="Incident workspace views">
          {workspaceTabs.map((tab) => (
            <Button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              variant={activeTab === tab.key ? "primary" : "secondary"}
              onClick={() => {
                if (tab.key === "ops") {
                  setOpsLoading(true);
                }
                setActiveTab(tab.key);
              }}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {error && !loading && (
          <div role="alert" style={{ color: colors.danger }}>
            {error}
          </div>
        )}

        {activeTab === "register" && (
          <div style={{ display: "grid", gap: spacing.lg }}>
            <div style={registerToolbarStyle}>
              <div style={registerToolbarRowStyle}>
                <Input
                  ariaLabel="Search incidents"
                  value={filters.q}
                  onChange={(e) => updateFilters((prev) => ({ ...prev, q: e.target.value }))}
                  placeholder="Search incidents..."
                  style={registerToolbarSearchStyle}
                />
                <Select
                  ariaLabel="Filter by status"
                  value={filters.status}
                  onChange={(e) => updateFilters((prev) => ({ ...prev, status: e.target.value, open_only: false }))}
                  style={registerToolbarSelectStyle}
                >
                  <option value="">All statuses</option>
                  {statusOptions.filter(Boolean).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
                <Select
                  ariaLabel="Filter by severity"
                  value={filters.severity}
                  onChange={(e) =>
                    updateFilters((prev) => ({ ...prev, severity: e.target.value, severity_values: [] }))
                  }
                  style={registerToolbarSelectStyle}
                >
                  <option value="">All severities</option>
                  {severityOptions.filter(Boolean).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
                <div style={registerToolbarMenuStyle} ref={moreFiltersRef}>
                  <Button
                    variant="secondary"
                    aria-haspopup="dialog"
                    aria-expanded={isMoreFiltersOpen}
                    aria-controls="incident-more-filters-panel"
                    onClick={() => {
                      setIsSavedViewsOpen(false);
                      setIsMoreFiltersOpen((current) => !current);
                    }}
                    style={
                      isMoreFiltersOpen || advancedFilterCount > 0
                        ? {
                            borderColor: colors.accent,
                            background: colors.accentSubtle,
                          }
                        : undefined
                    }
                  >
                    {moreFiltersLabel}
                  </Button>
                  {isMoreFiltersOpen && (
                    <div
                      id="incident-more-filters-panel"
                      role="dialog"
                      aria-label="More incident filters"
                      style={registerToolbarMenuPanelStyle}
                    >
                      <div style={registerToolbarMenuGridStyle}>
                        <Select
                          label="Priority"
                          ariaLabel="Filter by priority"
                          value={filters.priority}
                          onChange={(e) => updateFilters((prev) => ({ ...prev, priority: e.target.value }))}
                        >
                          <option value="">All priorities</option>
                          {priorityOptions.filter(Boolean).map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </Select>
                        <Input
                          label="Owner"
                          ariaLabel="Filter by owner"
                          value={filters.owner_id}
                          onChange={(e) => updateFilters((prev) => ({ ...prev, owner_id: e.target.value }))}
                          placeholder="Owner username"
                        />
                        <Input
                          type="date"
                          label="Created from"
                          value={filters.created_from}
                          onChange={(e) => updateFilters((prev) => ({ ...prev, created_from: e.target.value }))}
                          placeholder="Created from"
                        />
                        <Input
                          type="date"
                          label="Created to"
                          value={filters.created_to}
                          onChange={(e) => updateFilters((prev) => ({ ...prev, created_to: e.target.value }))}
                          placeholder="Created to"
                        />
                        <Select
                          label="Sort"
                          ariaLabel="Sort incidents"
                          value={filters.sort}
                          onChange={(e) => updateFilters((prev) => ({ ...prev, sort: e.target.value }))}
                        >
                          {sortOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div style={{ display: "flex", gap: spacing.md, flexWrap: "wrap" }}>
                        <label style={registerToolbarCheckboxStyle}>
                          <input
                            type="checkbox"
                            checked={filters.breached}
                            onChange={(e) => updateFilters((prev) => ({ ...prev, breached: e.target.checked }))}
                          />
                          Breached
                        </label>
                        <label style={registerToolbarCheckboxStyle}>
                          <input
                            type="checkbox"
                            checked={filters.escalated}
                            onChange={(e) => updateFilters((prev) => ({ ...prev, escalated: e.target.checked }))}
                          />
                          Escalated
                        </label>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.sm, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={registerToolbarMetaTextStyle}>Hidden filters stay applied until you clear them.</div>
                        <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                          <Button onClick={resetAllFilters} variant="secondary">
                            Reset
                          </Button>
                          <Button onClick={() => setIsMoreFiltersOpen(false)} variant="secondary">
                            Done
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div style={registerToolbarMenuStyle} ref={savedViewsRef}>
                  <Button
                    variant="secondary"
                    aria-haspopup="dialog"
                    aria-expanded={isSavedViewsOpen}
                    aria-controls="incident-saved-views-panel"
                    onClick={() => {
                      setIsMoreFiltersOpen(false);
                      setIsSavedViewsOpen((current) => !current);
                    }}
                    style={
                      selectedSavedView || isSavedViewsOpen
                        ? {
                            borderColor: colors.accent,
                            background: colors.accentSubtle,
                          }
                        : undefined
                    }
                  >
                    Saved Views
                  </Button>
                  {isSavedViewsOpen && (
                    <div
                      id="incident-saved-views-panel"
                      role="dialog"
                      aria-label="Incident saved views"
                      style={registerToolbarMenuPanelStyle}
                    >
                      <Select
                        label="Load saved view"
                        value={selectedSavedViewId}
                        onChange={(e) => {
                          const selected = savedViews.find((view) => String(view.id) === e.target.value);
                          if (selected) {
                            applySavedView(selected);
                          } else {
                            setSelectedSavedViewId("");
                          }
                        }}
                      >
                        <option value="">Select saved view</option>
                        {savedViews.map((view) => (
                          <option key={view.id} value={view.id}>
                            {view.name}
                          </option>
                        ))}
                      </Select>
                      {canEdit ? (
                        <>
                          <Input
                            label="Current view name"
                            value={savedViewName}
                            onChange={(e) => setSavedViewName(e.target.value)}
                            placeholder="Current view name"
                          />
                          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                            <Button onClick={() => saveCurrentView("create")} variant="secondary">
                              Save New
                            </Button>
                            <Button
                              onClick={() => saveCurrentView("update")}
                              variant="secondary"
                              disabled={!selectedSavedView}
                            >
                              Update
                            </Button>
                            <Button onClick={removeSavedView} variant="secondary" disabled={!selectedSavedView}>
                              Delete
                            </Button>
                          </div>
                        </>
                      ) : null}
                      {selectedSavedView && (
                        <div style={registerToolbarMetaTextStyle}>Current saved view: {selectedSavedView.name}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={registerQuickViewsStyle}>
                <div style={registerQuickViewButtonsStyle}>
                  <Button
                    onClick={() => applyQueue("my-open")}
                    variant={activeQuickView === "my-open" ? "primary" : "secondary"}
                    aria-pressed={activeQuickView === "my-open"}
                  >
                    My Open Incidents
                  </Button>
                  <Button
                    onClick={() => applyQueue("high-critical")}
                    variant={activeQuickView === "high-critical" ? "primary" : "secondary"}
                    aria-pressed={activeQuickView === "high-critical"}
                  >
                    High/Critical Open
                  </Button>
                  <Button
                    onClick={() => applyQueue("breached")}
                    variant={activeQuickView === "breached" ? "primary" : "secondary"}
                    aria-pressed={activeQuickView === "breached"}
                  >
                    Breached Incidents
                  </Button>
                  <Button
                    onClick={() => applyQueue("recently-closed")}
                    variant={activeQuickView === "recently-closed" ? "primary" : "secondary"}
                    aria-pressed={activeQuickView === "recently-closed"}
                  >
                    Recently Closed
                  </Button>
                </div>
                {reporting.overview && (
                  <div style={registerOperationalSummaryStyle} aria-label="Operational summary">
                    <span style={registerOperationalPillStyle}>Breached {reporting.overview?.breached_count ?? "-"}</span>
                    <span style={registerOperationalPillStyle}>Escalated {reporting.overview?.escalated_count ?? "-"}</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ ...cardStyle, overflow: "hidden" }}>
              <div style={tableCardBodyStyle}>
                {loading ? (
                  <Loader label="Loading incidents..." />
                ) : incidents.length === 0 ? (
                  <EmptyState title="No incidents found" />
                ) : (
                  <div style={{ display: "grid", gap: spacing.md }}>
                    <Table
                      columns={incidentColumns}
                      data={incidents}
                      getRowKey={(row) => row.id}
                      onRowClick={(row) => {
                        if (!row?.id) return;
                        if (isIncidentRoute) {
                          navigate(`${INCIDENTS_BASE_ROUTE}/${row.id}`);
                          return;
                        }
                        setSelectedId(row.id);
                      }}
                      getRowProps={(row) => ({
                        "aria-label": `Open incident ${row.title || row.id}`,
                        "aria-selected": String(selectedIncidentId) === String(row.id),
                        style:
                          String(selectedIncidentId) === String(row.id)
                            ? { ...registerTableRowStyle, ...registerTableSelectedRowStyle }
                            : registerTableRowStyle,
                      })}
                      caption="Incident register"
                    />
                    <div style={opsPaginationBarStyle}>
                      <div style={opsSubtitleStyle}>
                        Page {registerCurrentPage} of {registerTotalPages}
                        {registerPage.total > 0 ? ` • Showing ${registerPageStart}-${registerPageEnd} of ${registerPage.total}` : ""}
                      </div>
                      <div style={{ display: "flex", gap: spacing.sm }}>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={registerPage.offset <= 0}
                          onClick={() =>
                            setRegisterPage((current) => ({
                              ...current,
                              offset: Math.max(0, current.offset - current.limit),
                            }))
                          }
                        >
                          {"<- Prev"}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={registerPage.offset + registerPage.limit >= registerPage.total}
                          onClick={() =>
                            setRegisterPage((current) => ({
                              ...current,
                              offset: current.offset + current.limit,
                            }))
                          }
                        >
                          Next ->
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "reporting" && (
          <div style={{ ...cardStyle, ...surfaceCardBodyStyle, display: "grid", gap: spacing.xl }}>
            <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Operational Overview</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: spacing.sm }}>
              <MetricCard label="Breached" value={reporting.overview?.breached_count ?? "-"} />
              <MetricCard label="Escalated" value={reporting.overview?.escalated_count ?? "-"} />
              <MetricCard label="Avg Close (hrs)" value={reporting.overview?.average_time_to_close_hours ?? "-"} />
              <MetricCard label="Avg Open Age (hrs)" value={reporting.overview?.average_open_age_hours ?? "-"} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: spacing.xl }}>
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: spacing.sm, color: colors.text }}>Open by Status</div>
                {(reporting.overview?.open_by_status &&
                  Object.entries(reporting.overview.open_by_status).map(([bucket, count]) => (
                    <div key={bucket} style={{ display: "flex", justifyContent: "space-between", fontSize: typography.size.sm, color: colors.text }}>
                      <span>{bucket}</span>
                      <span>{count}</span>
                    </div>
                  ))) || <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>No data</div>}
              </div>
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: spacing.sm, color: colors.text }}>Open by Severity</div>
                {(reporting.overview?.open_by_severity &&
                  Object.entries(reporting.overview.open_by_severity).map(([bucket, count]) => (
                    <div key={bucket} style={{ display: "flex", justifyContent: "space-between", fontSize: typography.size.sm, color: colors.text }}>
                      <span>{bucket}</span>
                      <span>{count}</span>
                    </div>
                  ))) || <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>No data</div>}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: spacing.xl }}>
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: spacing.sm, color: colors.text }}>Created Trend</div>
                {(reporting.trends?.created_over_time || []).slice(-5).map((row) => (
                  <div key={row.bucket} style={{ display: "flex", justifyContent: "space-between", fontSize: typography.size.sm, color: colors.text }}>
                    <span>{row.bucket}</span>
                    <span>{row.count}</span>
                  </div>
                ))}
                {(reporting.trends?.created_over_time || []).length === 0 && (
                  <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>No data</div>
                )}
              </div>
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: spacing.sm, color: colors.text }}>Owner Workload</div>
                {(reporting.workload?.owners || []).slice(0, 5).map((row, index) => (
                  <div key={`${row.owner_id || "unassigned"}-${index}`} style={{ display: "flex", justifyContent: "space-between", fontSize: typography.size.sm, color: colors.text }}>
                    <span>{row.owner_id || "Unassigned"}</span>
                    <span>{row.open_count}</span>
                  </div>
                ))}
                {(reporting.workload?.owners || []).length === 0 && (
                  <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>No data</div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "ops" && (
          <div style={{ ...cardStyle, ...surfaceCardBodyStyle, display: "grid", gap: spacing.lg }}>
            <div style={{ display: "grid", gap: spacing.xs }}>
              <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Incident Ops Health</div>
              <div style={opsSubtitleStyle}>{opsNeedsAttentionSubtitle}</div>
            </div>

            <div style={opsSummaryGridStyle} aria-label="Operational incident summary">
              <div style={opsSummaryBlockStyle}>
                <div style={opsSummaryLabelStyle}>Breached</div>
                <div style={opsSummaryCountStyle}>{formatOpsCount(opsSummary.breachedCount)}</div>
              </div>
              <div style={opsSummaryBlockStyle}>
                <div style={opsSummaryLabelStyle}>Escalated</div>
                <div style={opsSummaryCountStyle}>{formatOpsCount(opsSummary.escalatedCount)}</div>
              </div>
              <div style={opsSummaryBlockStyle}>
                <div style={opsSummaryLabelStyle}>High/Critical Open</div>
                <div style={opsSummaryCountStyle}>{formatOpsCount(opsSummary.highCriticalCount)}</div>
              </div>
            </div>

            {opsSummaryError ? (
              <div role="status" style={{ color: colors.textMuted, fontSize: typography.size.sm }}>
                {opsSummaryError}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: spacing.md }}>
              <div style={{ display: "grid", gap: spacing.xs }}>
                <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{activeOpsDefinition.label}</div>
                <div style={opsSubtitleStyle}>Operational queues keep the focus on current incidents that require intervention.</div>
              </div>
              <div style={opsQueueButtonsStyle}>
                {opsQueueDefinitions.map((queue) => (
                  <Button
                    key={queue.key}
                    type="button"
                    variant={activeOpsQueue === queue.key ? "primary" : "secondary"}
                    aria-pressed={activeOpsQueue === queue.key}
                    onClick={() => {
                      if (activeOpsQueue === queue.key) return;
                      setOpsLoading(true);
                      setOpsQueueError("");
                      setActiveOpsQueue(queue.key);
                      setOpsQueueOffset(0);
                      setSelectedId(null);
                    }}
                  >
                    {queue.label} {formatOpsCount(opsQueueCounts[queue.key])}
                  </Button>
                ))}
              </div>
            </div>

            <div style={{ ...cardStyle, overflow: "hidden" }}>
              <div style={tableCardBodyStyle}>
                {opsLoading ? (
                  <Loader label="Loading operational incidents..." />
                ) : opsQueueError ? (
                  <EmptyState
                    title="Unable to load Ops Health"
                    description="Operational incidents could not be loaded right now."
                    action={
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setOpsLoading(true);
                          Promise.all([
                            loadOpsSummary(),
                            loadOpsQueue(activeOpsQueue, opsQueueOffset),
                            activeOpsQueue === DEFAULT_OPS_QUEUE ? Promise.resolve() : loadOpsNeedsAttentionTotal(),
                          ]);
                        }}
                      >
                        Retry
                      </Button>
                    }
                    compact
                  />
                ) : opsQueue.items.length === 0 ? (
                  <EmptyState
                    title={activeOpsDefinition.emptyTitle}
                    description={activeOpsDefinition.emptyDescription}
                    compact
                  />
                ) : (
                  <div style={{ display: "grid", gap: spacing.md }}>
                    <Table
                      columns={opsIncidentColumns}
                      data={opsQueue.items}
                      getRowKey={(row) => row.id}
                      onRowClick={(row) => {
                        if (!row?.id) return;
                        if (isIncidentRoute) {
                          navigate(`${INCIDENTS_BASE_ROUTE}/${row.id}`);
                          return;
                        }
                        setSelectedId(row.id);
                      }}
                      getRowProps={(row) => ({
                        "aria-label": `Open incident ${row.title || row.id}`,
                        "aria-selected": String(selectedIncidentId) === String(row.id),
                        style:
                          String(selectedIncidentId) === String(row.id)
                            ? { ...registerTableRowStyle, ...registerTableSelectedRowStyle }
                            : registerTableRowStyle,
                      })}
                      caption={`${activeOpsDefinition.label} incidents`}
                    />

                    <div style={opsPaginationBarStyle}>
                      <div style={opsSubtitleStyle}>
                        Page {opsCurrentPage} of {opsTotalPages}
                        {opsQueue.total > 0 ? ` • Showing ${opsPageStart}-${opsPageEnd} of ${opsQueue.total}` : ""}
                      </div>
                      <div style={{ display: "flex", gap: spacing.sm }}>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={opsQueue.offset <= 0}
                          onClick={() => {
                            setOpsLoading(true);
                            setOpsQueueError("");
                            setOpsQueueOffset((currentOffset) => Math.max(0, currentOffset - opsQueue.limit));
                            setSelectedId(null);
                          }}
                        >
                          {"<- Prev"}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={opsQueue.offset + opsQueue.limit >= opsQueue.total}
                          onClick={() => {
                            setOpsLoading(true);
                            setOpsQueueError("");
                            setOpsQueueOffset((currentOffset) => currentOffset + opsQueue.limit);
                            setSelectedId(null);
                          }}
                        >
                          Next ->
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      <IncidentDetailDrawer
        incident={activeTab === "register" || activeTab === "ops" ? selectedIncident : null}
        incidentId={activeTab === "register" || activeTab === "ops" ? selectedIncidentId : null}
        onClose={() => {
          if (routeIncidentId && isIncidentRoute) {
            navigate(INCIDENTS_BASE_ROUTE);
            return;
          }
          setSelectedId(null);
        }}
        onIncidentUpdated={handleIncidentUpdated}
        canEdit={canEdit}
        canManage={canManage}
        onOpenFullPage={
          !routeIncidentId
            ? (id) => {
                if (!id) return;
                navigate(`${INCIDENTS_BASE_ROUTE}/${id}`);
              }
            : undefined
        }
      />

      {showCreate && canEdit && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div style={{ width: "520px", maxWidth: "90vw" }}>
            <IncidentForm
              onSaved={async () => {
                setShowCreate(false);
                setEditingIncident(null);
                await refreshAll(filters);
              }}
              onCancel={() => {
                setShowCreate(false);
                setEditingIncident(null);
              }}
              incident={editingIncident}
            />
          </div>
        </div>
      )}
    </div>
  );
}
